import { TrashIcon } from '@heroicons/react/24/outline'
import { PlainInput } from 'components/PlainInput'
import { useMemo } from 'react'
import { Button, SearchableSelect } from 'stories/components'
import { confirm } from 'utils'

import type { IHubspotSourceKey } from './constants'
import { IMappingRow, rowsFromRecord } from './mapping'

const CUSTOM = '__custom__'

interface MappingTableProps {
  title: string
  description: string
  rows: IMappingRow[]
  sourceKeys: Record<string, IHubspotSourceKey>
  starter: Record<string, string>
  onChange: (rows: IMappingRow[]) => void
}

export const MappingTable = ({ title, description, rows, sourceKeys, starter, onChange }: MappingTableProps) => {
  const sourceOptions = useMemo(() => {
    const options: Record<string, string> = {}
    Object.keys(sourceKeys)
      .sort((a, b) => (sourceKeys[a].label > sourceKeys[b].label ? 1 : -1))
      .forEach((key) => (options[key] = `${sourceKeys[key].label} (${key})`))
    options[CUSTOM] = 'Custom expression ...'
    return options
  }, [sourceKeys])

  const duplicates = useMemo(() => {
    const seen: Record<string, number> = {}
    rows.forEach(({ property }) => {
      const name = (property || '').trim()
      if (name) seen[name] = (seen[name] || 0) + 1
    })
    return new Set(Object.keys(seen).filter((name) => seen[name] > 1))
  }, [rows])

  const update = (index: number, values: Partial<IMappingRow>) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...values } : row))
    onChange(next)
  }

  const onChangeSource = (index: number, value: string) => {
    if (value === CUSTOM) return update(index, { custom: true })
    update(index, { custom: false, expression: value })
  }

  const onRemove = async (index: number) => {
    const { property } = rows[index]
    if (property && !(await confirm(`Stop sending "${property}" to HubSpot?`))) return
    onChange(rows.filter((_, i) => i !== index))
  }

  const onAdd = () => onChange([...rows, { property: '', expression: '', custom: false }])

  const onLoadStarter = async () => {
    const starterRows = rowsFromRecord(starter, sourceKeys)
    if (!starterRows.length) return
    const mapped = rows.filter((row) => row.property.trim())
    if (mapped.length && !(await confirm(`Replace the ${mapped.length} mapped properties with the starter set?`)))
      return
    onChange(starterRows)
  }

  return (
    <div className="mb-8">
      <p className="text-md font-bold border-b mb-1 pb-1">{title}</p>
      <p className="text-sm text-gray-600 mb-3">{description}</p>

      <table className="text-sm w-full">
        <thead className="uppercase text-xs bg-gray-50">
          <tr>
            <th className="p-3 border w-12">No</th>
            <th className="p-3 border text-left">HubSpot Property</th>
            <th className="p-3 border text-left">Nexys Source</th>
            <th className="p-3 border w-16">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const { property, expression, custom } = row
            const isDuplicate = duplicates.has((property || '').trim())
            return (
              <tr key={index}>
                <td className="p-2 border text-center">{index + 1}</td>
                <td className="px-3 border">
                  <PlainInput
                    value={property}
                    content={property || '- property -'}
                    className={isDuplicate ? 'text-red-600' : ''}
                    onChange={(value: string) => update(index, { property: value })}
                  />
                  {isDuplicate && <p className="text-xs text-red-600 pb-1">Mapped twice, the lower row wins.</p>}
                </td>
                <td className="px-3 border py-1">
                  <SearchableSelect
                    id={`source-${index}`}
                    title=""
                    value={custom ? CUSTOM : expression}
                    options={sourceOptions}
                    hasDefaultOption
                    defaultOptionText="- Select -"
                    onChange={(value: string) => onChangeSource(index, value)}
                  />
                  {custom && (
                    <PlainInput
                      value={expression}
                      content={expression || '- expression -'}
                      className="font-mono text-xs"
                      onChange={(value: string) => update(index, { expression: value })}
                    />
                  )}
                </td>
                <td className="px-3 border text-center">
                  <span className="btn-icon" onClick={() => onRemove(index)}>
                    <TrashIcon className="w-4 h-4 text-red-500" />
                  </span>
                </td>
              </tr>
            )
          })}
          {!rows.length && (
            <tr>
              <td className="p-3 border text-center text-gray-500" colSpan={4}>
                Nothing mapped yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex items-center gap-4">
        <Button link onClick={onAdd}>
          + Add Property
        </Button>
        <Button link onClick={onLoadStarter}>
          Load Starter Mapping
        </Button>
      </div>
    </div>
  )
}
